import type { ProjectHealth } from "@codvero/shared";
import { useQuery } from "@tanstack/react-query";
import { Activity, Cpu, HardDrive, KeyRound, Network, RefreshCw, Wrench } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { projectsApi } from "@/api/projects.ts";
import { cn } from "@/lib/cn.ts";
import { Spinner } from "@/ui/primitives.tsx";
import { cpuShare } from "@/lib/cpu.ts";

function fmtMb(mb: number | null) {
  if (mb === null) return "—";
  return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb} MB`;
}
function fmtUptime(sec: number | null, t: TFunction) {
  if (sec === null) return "—";
  const d = Math.floor(sec / 86400), h = Math.floor((sec % 86400) / 3600), m = Math.floor((sec % 3600) / 60);
  return d > 0 ? t("health.uptimeD", { d, h }) : h > 0 ? t("health.uptimeH", { h, m }) : t("health.uptimeM", { m });
}

/** Container facts the original's Overview tab showed: resources, disk, network, tooling, auth files. */
export function HealthCenter({ projectId, running }: { projectId: string; running: boolean }) {
  const { t } = useTranslation();
  const q = useQuery({ queryKey: ["projects", projectId, "health"], queryFn: () => projectsApi.health(projectId), refetchInterval: running ? 30_000 : false });
  const h = q.data;
  return (
    <section className="mt-10">
      <div className="flex items-center gap-2">
        <h2 className="text-xs font-semibold uppercase tracking-widest text-fg-muted">{t("health.title")}</h2>
        <button type="button" aria-label={t("git.refresh")} className="rounded-full border border-border p-1 text-fg-muted hover:text-fg" onClick={() => void q.refetch()}><RefreshCw className={cn("size-3.5", q.isFetching && "animate-spin")} /></button>
        {h && <span className="ms-auto text-[11px] text-fg-muted">{new Date(h.checkedAt).toLocaleTimeString()}</span>}
      </div>
      {!h && <div className="mt-3 rounded-xl border border-border bg-surface p-4">{q.isPending ? <Spinner /> : <span className="text-sm text-fg-muted">—</span>}</div>}
      {h && (
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <Panel icon={Cpu} title={t("health.resources")}>
            <Row k={t("health.state")} v={t(`projects.state.${h.container.state}`)} />
            <Row k={t("health.uptime")} v={fmtUptime(h.uptimeSec, t)} />
            <Row k="CPU" v={((c) => (!c ? "—" : c.cores ? `${c.pct}% · ${t("machines.coresOf", { used: c.used, cores: c.cores })}${h.limits.cpus ? "" : ` (${t("projectSettings.unlimitedValue")})`}` : `${h.container.cpuPercent}%`))(cpuShare(h.container.cpuPercent, h.cpuCores))} bar={((c) => (c?.cores ? c.pct / 100 : null))(cpuShare(h.container.cpuPercent, h.cpuCores))} />
            <Row k={t("health.memory")} v={`${fmtMb(h.container.memoryUsedMb)} / ${fmtMb(h.container.memoryLimitMb ?? h.limits.memoryMb)}`} bar={h.container.memoryUsedMb !== null && (h.container.memoryLimitMb ?? h.limits.memoryMb) ? h.container.memoryUsedMb / (h.container.memoryLimitMb ?? h.limits.memoryMb) : null} />
            <Row k={t("health.pids")} v={h.container.pids === null ? "—" : `${h.container.pids} / ${h.limits.pids || t("projectSettings.unlimitedValue")}`} />
            <Row k={t("health.image")} v={h.container.image ?? "—"} mono />
            <Row k="ID" v={h.container.containerId?.slice(0, 12) ?? "—"} mono />
          </Panel>
          <Panel icon={HardDrive} title={t("health.disk")}>
            {h.disks.length === 0 && <p className="text-xs text-fg-muted">—</p>}
            {h.disks.map((d) => <Row key={d.mount} k={d.mount} v={`${fmtMb(d.usedMb)} / ${fmtMb(d.totalMb)}`} bar={d.usedMb !== null && d.totalMb ? d.usedMb / d.totalMb : null} mono />)}
            <Row k={t("health.workspaceSize")} v={fmtMb(h.workspaceMb)} />
          </Panel>
          <Panel icon={Network} title={t("health.network")}>
            <Row k={t("health.netName")} v={h.network.name} mono />
            <Row k="IP" v={h.network.ip ?? "—"} mono />
            <Row k={t("health.gateway")} v={h.network.gatewayPort ? `127.0.0.1:${h.network.gatewayPort}` : t("health.gatewayOff")} mono />
            <Row k={t("health.internet")} v={<span className={cn(h.network.internet === "ok" ? "text-accent" : h.network.internet === "fail" ? "text-danger" : "text-fg-muted")}>{t(`health.internet_${h.network.internet}`)}</span>} />
            <Row k={t("health.listening")} v={h.network.listeningPorts.length ? h.network.listeningPorts.map((p) => `:${p}`).join("  ") : "—"} mono />
          </Panel>
          <Panel icon={Wrench} title={t("health.tooling")}>
            <div className="grid grid-cols-2 gap-x-4">
              {h.tools.map((x) => <Row key={x.name} k={x.name} v={x.version ?? <span className="text-fg-muted">{t("health.missing")}</span>} mono />)}
            </div>
          </Panel>
          <Panel icon={KeyRound} title={t("health.auth")}>
            <Row k={t("health.accountMounted")} v={<Dot ok={h.auth.accountMounted} />} />
            <Row k={t("health.credentials")} v={<Dot ok={h.auth.credentialsPresent} />} />
            <p className="mt-1 text-[11px] text-fg-muted">{t("health.authHint")}</p>
          </Panel>
          <Panel icon={Activity} title={t("health.limits")}>
            <Row k="CPU" v={h.limits.cpus ? `${h.limits.cpus} vCPU` : t("projectSettings.unlimitedValue")} />
            <Row k={t("health.memory")} v={h.limits.memoryMb ? fmtMb(h.limits.memoryMb) : t("projectSettings.unlimitedValue")} />
            <Row k={t("health.pids")} v={h.limits.pids ? String(h.limits.pids) : t("projectSettings.unlimitedValue")} />
          </Panel>
        </div>
      )}
    </section>
  );
}

function Panel({ icon: Icon, title, children }: { icon: typeof Cpu; title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-border bg-surface p-4">
      <h3 className="mb-2 flex items-center gap-2 text-sm font-medium"><Icon className="size-4 text-fg-muted" /> {title}</h3>
      <div className="space-y-1">{children}</div>
    </div>
  );
}
function Row({ k, v, mono, bar }: { k: string; v: React.ReactNode; mono?: boolean; bar?: number | null }) {
  return (
    <div className="text-xs">
      <div className="flex items-center gap-3">
        <span className="w-32 shrink-0 text-fg-muted">{k}</span>
        <span dir="ltr" className={cn("min-w-0 flex-1 truncate text-start", mono && "font-mono")}>{v}</span>
      </div>
      {bar !== null && bar !== undefined && <div className="mt-0.5 ms-32 h-1 overflow-hidden rounded bg-surface-2"><div className={cn("h-full", bar > 0.9 ? "bg-danger" : bar > 0.7 ? "bg-warn" : "bg-accent")} style={{ width: `${Math.min(100, Math.round(bar * 100))}%` }} /></div>}
    </div>
  );
}
function Dot({ ok }: { ok: boolean }) {
  const { t } = useTranslation();
  return <span className={cn("inline-flex items-center gap-1.5", ok ? "text-accent" : "text-fg-muted")}><span className={cn("size-1.5 rounded-full", ok ? "bg-accent" : "bg-fg-muted/50")} />{ok ? t("common.yes") : t("common.no")}</span>;
}

export type { ProjectHealth };
